import React, { useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Heart,
  Briefcase,
  TrendingUp,
  ArrowRight,
  Building2,
  MapPin,
  DollarSign,
  Upload,
  Download,
  FileEdit,
  Edit
} from 'lucide-react';
import { useApp } from '@/pages/providers/AppProvider';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthContext';
import { normalizeMediaUrl } from '@/utils/media';
import { useImageUpdate } from '@/context/ImageUpdateContext';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Progress } from '../ui/progress';
import { safeStorage } from '../../lib/safe-storage';

export function DashboardHome() {
  const navigate = useNavigate();
  const { applications, jobs, savedJobs, currentUser, getProfileCompletion, resumes } = useApp();

  // Get the latest resume for detailed profile display
  const latestResume = React.useMemo(() => {
    if (!resumes || resumes.length === 0) return null;
    return [...resumes].sort((a, b) => {
      const ad = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
      const bd = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
      return bd - ad;
    })[0];
  }, [resumes]);

  const completion = React.useMemo(() => getProfileCompletion(currentUser), [currentUser, getProfileCompletion, resumes]);
  const { user, uploadProfileImage } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>(() => normalizeMediaUrl((user as any)?.profileImage || (user as any)?.profile_image || (user as any)?.profile_image_url) || '/placeholder-user.png');
  const { triggerImageUpdate } = useImageUpdate();

  React.useEffect(() => {
    const url = normalizeMediaUrl((user as any)?.profileImage || (user as any)?.profile_image || (user as any)?.profile_image_url) || '/placeholder-user.png';
    setAvatarUrl(url);
  }, [user]);

  // Calculate stats
  const stats = {
    totalApplications: applications?.length || 0,
    pending: applications?.filter(a => a.status === 'pending').length || 0,
    accepted: applications?.filter(a => a.status === 'accepted').length || 0,
    rejected: applications?.filter(a => a.status === 'rejected').length || 0,
    savedJobs: savedJobs?.length || 0
  };

  // Get recent applications
  const recentApplications = applications?.slice(0, 3) || [];

  // Get recommended jobs (exclude applied and saved)
  const appliedJobIds = new Set(applications?.map(a => a.jobId) || []);
  const savedJobIds = new Set(savedJobs || []);
  const recommendedJobs = jobs?.filter(
    j => !appliedJobIds.has(j.id) && !savedJobIds.has(j.id)
  ).slice(0, 3) || [];

  const getStatusConfig = (status: string) => {
    const configs: any = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
      reviewed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle2, label: 'Reviewed' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle2, label: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' }
    };
    return configs[status] || configs.pending;
  };

  const StatCard = ({ title, count, icon: Icon, color, onClick }: any) => {
    const palette: Record<string, { bg: string; text: string; ring: string }> = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-200' },
      yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', ring: 'ring-yellow-200' },
      green: { bg: 'bg-green-50', text: 'text-green-600', ring: 'ring-green-200' },
      red: { bg: 'bg-red-50', text: 'text-red-600', ring: 'ring-red-200' }
    };
    const s = palette[color] || palette.blue;
    return (
      <Card
        className={`cursor-pointer rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200 ring-1 ${s.ring}`}
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${s.bg}`}>
              <Icon className={`h-5 w-5 ${s.text}`} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</p>
              <p className="text-xl md:text-2xl font-bold text-gray-800">{count}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const handleGoToLatestResumeBuilder = async () => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      // Get all resumes
      const json = await apiClient.request('/resumes');
      const list = Array.isArray((json as any)?.resumes) ? (json as any).resumes : (Array.isArray((json as any)?.data) ? (json as any).data : (Array.isArray(json) ? (json as any) : []));

      let resumeId: string;

      if (!list.length) {
        // Create a new resume automatically if none exists
        toast.loading('Creating your profile...', { id: 'create-resume' });
        const res = await apiClient.request('/resumes', {
          method: 'POST',
          body: JSON.stringify({ title: 'My Professional Resume', template: 'modern' })
        });
        const created = (res && (res.data ?? res)) as any;
        resumeId = created?._id || created?.id;
        toast.success('Ready to build your profile!', { id: 'create-resume' });
      } else {
        // Sort to get the latest updated one
        const sorted = [...list].sort((a: any, b: any) => {
          const ad = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
          const bd = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
          return bd - ad;
        });
        const latest = sorted[0];
        resumeId = latest?._id || latest?.id;
      }

      if (!resumeId) {
        toast.error('Could not access resume builder');
        return;
      }

      navigate(`/resume-builder/${resumeId}`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to open resume builder');
    }
  };

  const handleDownloadLatestResume = async () => {
    try {
      const token = safeStorage.getItem('megajobnepal_auth_token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }
      const json = await apiClient.request('/resumes');
      const list = Array.isArray((json as any)?.resumes) ? (json as any).resumes : (Array.isArray((json as any)?.data) ? (json as any).data : (Array.isArray(json) ? (json as any) : []));
      if (!list.length) {
        toast.error('No resume found');
        return;
      }
      const sorted = [...list].sort((a: any, b: any) => {
        const ad = new Date(a.updated_at || a.updatedAt || a.created_at || a.createdAt || 0).getTime();
        const bd = new Date(b.updated_at || b.updatedAt || b.created_at || b.createdAt || 0).getTime();
        return bd - ad;
      });
      const latest = sorted[0];
      const id = latest?._id || latest?.id;
      if (!id) {
        toast.error('Invalid resume data');
        return;
      }
      const blob = await apiClient.requestBlob(`/resumes/${id}/export/pdf`);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const name = (latest?.personalInfo?.fullName || latest?.title || 'resume') as string;
      a.href = url;
      a.download = `${name}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Downloading resume');
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download resume');
    }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white border-0 rounded-2xl shadow-lg">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">Welcome Back!</h2>
              <p className="text-blue-100 mt-1">Track your applications and discover new opportunities</p>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <div
                className="text-right cursor-pointer hover:bg-white/10 p-2 rounded-xl transition-all group"
                onClick={handleGoToLatestResumeBuilder}
                title="Click to complete your profile"
              >
                <div className="text-sm text-blue-100 group-hover:text-white transition-colors">Profile Completion</div>
                <div className="flex items-center gap-3 mt-1">
                  <div className="w-40">
                    <Progress value={Math.max(0, Math.min(100, Number(completion || 0)))} className="bg-white/20" />
                  </div>
                  <div className="text-2xl font-bold">{Math.round(Number(completion || 0))}%</div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 rounded-2xl border border-gray-200 shadow-sm">
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="font-semibold">Profile</CardTitle>
            <Button variant="outline" size="sm" onClick={() => navigate('/jobseeker-dashboard/edit-profile')}>
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-start gap-6">
              <div className="relative">
                <div className="w-24 h-24 rounded-full overflow-hidden ring-2 ring-blue-200">
                  <img
                    src={avatarUrl}
                    className="w-full h-full object-cover"
                    alt="Profile"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3 w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Uploading…' : 'Upload Image'}
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      setUploading(true);
                      const res = await uploadProfileImage(file);
                      if (res?.error) {
                        toast.error(res.error);
                      } else {
                        toast.success('Profile image updated');
                        if (res?.imageUrl) {
                          const base = normalizeMediaUrl(res.imageUrl) || res.imageUrl;
                          const sep = String(base).includes('?') ? '&' : '?';
                          setAvatarUrl(`${base}${sep}v=${Date.now()}`);
                          triggerImageUpdate();
                        }
                      }
                    } catch (err: any) {
                      toast.error(err?.message || 'Failed to upload image');
                    } finally {
                      setUploading(false);
                    }
                  }}
                  className="hidden"
                />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900">
                  {latestResume?.personalInfo?.fullName || user?.full_name || user?.email || 'Your name'}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-blue-500" />
                    <span>{latestResume?.personalInfo?.city || latestResume?.personalInfo?.address || 'Location not set'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4 text-purple-500" />
                    <span className="capitalize">
                      {latestResume?.experienceStatus === 'fresher' ? 'Fresher' :
                        latestResume?.experienceStatus === 'student' ? 'Student' :
                          (latestResume?.experience?.[0]?.jobTitle ? `${latestResume.experience[0].jobTitle}` : 'Experience not set')}
                    </span>
                  </div>
                </div>

                {/* Dynamic Skills from Resume */}
                <div className="mt-6 border-t pt-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Top Skills</p>
                  <div className="flex flex-wrap gap-2">
                    {latestResume?.skills && latestResume.skills.length > 0 ? (
                      latestResume.skills.slice(0, 6).map((skill: any, idx: number) => (
                        <Badge key={idx} variant="secondary" className="bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-100">
                          {skill.name}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-gray-400 italic">No skills added to resume yet</span>
                    )}
                    {latestResume?.skills?.length > 6 && (
                      <span className="text-xs text-blue-500 font-medium ml-1">+{latestResume.skills.length - 6} more</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card className="rounded-2xl border border-gray-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" onClick={handleDownloadLatestResume}>
                <Download className="w-4 h-4 mr-2" />
                Download Resume
              </Button>
              <Button variant="outline" className="w-full justify-start" onClick={handleGoToLatestResumeBuilder}>
                <FileEdit className="w-4 h-4 mr-2" />
                Update Resume
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Total Applications"
          count={stats.totalApplications}
          icon={FileText}
          color="blue"
          onClick={() => navigate('/jobseeker-dashboard/applications')}
        />
        <StatCard
          title="Pending"
          count={stats.pending}
          icon={Clock}
          color="yellow"
          onClick={() => navigate('/jobseeker-dashboard/applications')}
        />
        <StatCard
          title="Accepted"
          count={stats.accepted}
          icon={CheckCircle2}
          color="green"
          onClick={() => navigate('/jobseeker-dashboard/applications')}
        />
        <StatCard
          title="Saved Jobs"
          count={stats.savedJobs}
          icon={Heart}
          color="red"
          onClick={() => navigate('/jobseeker-dashboard/saved')}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle>Recent Applications</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/jobseeker-dashboard/applications')}>
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {recentApplications.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No applications yet</p>
                <Button className="mt-4" onClick={() => navigate('/jobseeker-dashboard/jobs')}>
                  Browse Jobs
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentApplications.map((app) => {
                  const job = jobs?.find(j => j.id === app.jobId);
                  const statusConfig = getStatusConfig(app.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <div key={app.id} className="border rounded-lg p-4 hover:shadow-md transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Building2 className="h-6 w-6 text-gray-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{job?.title || 'Job Title'}</h3>
                            <p className="text-sm text-gray-600">{job?.company || 'Company'}</p>
                            <p className="text-xs text-gray-500 mt-1">Applied {new Date(app.appliedAt).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <Badge className={statusConfig.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig.label}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle>Recommended Jobs</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/jobseeker-dashboard/jobs')}>
                View All
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recommendedJobs.length === 0 ? (
                <div className="text-center py-8 col-span-2">
                  <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No jobs available</p>
                </div>
              ) : (
                recommendedJobs.map((job) => (
                  <div
                    key={job.id}
                    className="border rounded-lg p-3 hover:shadow-md transition-all cursor-pointer bg-white group"
                    onClick={() => {
                      // Use slug if available for prettier URLs
                      const target = (job as any).slug || job.id;
                      navigate(`/jobs/${target}`);
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-8 h-8 bg-gray-50 rounded-md flex items-center justify-center border border-gray-100">
                        {/* Attempt to show company logo if available, or fallback icon */}
                        {(job as any).company_logo || (job as any).logo ? (
                          <img src={normalizeMediaUrl((job as any).company_logo || (job as any).logo)} alt="" className="w-full h-full object-cover rounded-md" />
                        ) : (
                          <Briefcase className="h-4 w-4 text-gray-400" />
                        )}
                      </div>
                      <Badge variant="outline" className="text-[10px] px-1 h-5">{job.type || 'Full Time'}</Badge>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mb-0.5 truncate pr-2" title={job.title}>{job.title}</h3>
                    <p className="text-xs text-gray-500 mb-2 truncate" title={String(job.company)}>{job.company}</p>

                    <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
                      <div className="flex items-center gap-1 text-[10px] text-gray-400">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate max-w-[60px]">{job.location || 'Remote'}</span>
                      </div>
                      <span className="text-[10px] font-medium text-blue-600 group-hover:underline">View Details</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
